import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import moment from 'moment';
import { merge, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class DateRangePickerComponent implements OnInit, OnChanges {
  @Input() screenFilterForm: FormGroup;
  @Input() styleType: String = 'rounded';
  @Input() showFloatLabel = false;
  @Input() paramStartDate: string; // format: 'DD/MM/YYYY'
  @Input() paramEndDate: string; // format: 'DD/MM/YYYY'
  @Input() isRestrictedToParamDates: boolean = false;
  @Input() maxSelectableDate: Date | null = null;

  @Output() chosenDates = new EventEmitter<void>();

  constructor(private fb: FormBuilder) {}

  private componentDestroyed$: Subject<void> = new Subject();

  minDate: Date | null = null;
  maxDate: Date | null = null;

  dateRangeForm = this.fb.group({
    startDate: [null as Date | null, [Validators.required]],
    endDate: [null as Date | null, [Validators.required]],
  });

  /** =============================
   * INIT
   ============================= */
  ngOnInit(): void {
    this.updateValidators();
    this.initValueChange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.paramStartDate || changes.paramEndDate || changes.isRestrictedToParamDates) {
      this.setInitialDateRange();
      this.updateValidators();
      return;
    }

    if (changes.maxSelectableDate) {
      this.updateValidators();
    }
  }

  /** ------------------------------------------
   *   INITIALISE LES BORNES + VALEURS PAR DÉFAUT
   * ------------------------------------------ */
  private setInitialDateRange() {
    // ⚙️ Appliquer les bornes SEULEMENT si isRestrictedToParamDates est activé
    if (this.isRestrictedToParamDates && this.paramStartDate && this.paramEndDate) {
      this.minDate = moment(this.paramStartDate, 'DD/MM/YYYY').toDate();
      this.maxDate = moment(this.paramEndDate, 'DD/MM/YYYY').toDate();
    } else {
      this.minDate = null;
      this.maxDate = null;
    }

    if (this.paramStartDate) {
      const startDate = moment(this.paramStartDate, 'DD/MM/YYYY').toDate();
      this.dateRangeForm.get('startDate')?.setValue(startDate);
      this.screenFilterForm?.get('startDate')?.setValue(this.paramStartDate);
    }

    if (this.paramEndDate) {
      const endDate = moment(this.paramEndDate, 'DD/MM/YYYY').toDate();
      this.dateRangeForm.get('endDate')?.setValue(endDate);
      this.screenFilterForm?.get('endDate')?.setValue(this.paramEndDate);
    }
  }

  /** ------------------------------------------
   *   MISE À JOUR DES VALIDATEURS DU FORMULAIRE
   * ------------------------------------------ */
  private updateValidators() {
    this.dateRangeForm.clearValidators();

    const validators = [this.dateRangeValidator(this.isRestrictedToParamDates ? this.minDate : null, this.resolvedMaxDate)];

    this.dateRangeForm.setValidators(validators);
    this.dateRangeForm.updateValueAndValidity({ emitEvent: true });
  }

  /** ------------------------------------------------------------------
   * CREATION DU VALIDATEUR :
   *  1 - Start <= End
   *  2 - Optionnellement dans les bornes min/max
   * ------------------------------------------------------------------ */
  private dateRangeValidator(min: Date | null, max: Date | null): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const startCtrl = group.get('startDate');
      const endCtrl = group.get('endDate');

      const start = startCtrl?.value;
      const end = endCtrl?.value;

      if (!start || !end) return null;

      const startM = moment(start);
      const endM = moment(end);

      // --- 1 : start <= end ---
      if (startM.isAfter(endM)) {
        return { dateOrderInvalid: true };
      }

      // --- 2 : bornes min/max ---
      const minM = min ? moment(min).startOf('day') : null;
      const maxM = max ? moment(max).endOf('day') : null;

      if (
        (minM && (startM.isBefore(minM) || endM.isBefore(minM))) ||
        (maxM && (startM.isAfter(maxM) || endM.isAfter(maxM)))
      ) {
        return { dateOutOfRange: true };
      }

      return null;
    };
  }

  get resolvedMaxDate(): Date | null {
    if (this.maxSelectableDate) {
      return this.maxSelectableDate;
    }

    return this.isRestrictedToParamDates ? this.maxDate : null;
  }

  /** ------------------------------------------
   *   CHANGEMENTS FORMULAIRE = UPDATE OUTPUT
   * ------------------------------------------ */
  public initValueChange() {
    merge(this.dateRangeForm.get('startDate').valueChanges, this.dateRangeForm.get('endDate').valueChanges)
      .pipe(debounceTime(500), takeUntil(this.componentDestroyed$))
      .subscribe(
        () => {
          const startDate = moment(this.dateRangeForm.value.startDate).format('DD/MM/YYYY');
          const endDate = moment(this.dateRangeForm.value.endDate).format('DD/MM/YYYY');

          this.screenFilterForm.get('startDate').setValue(startDate);
          this.screenFilterForm.get('endDate').setValue(endDate);

          if (this.dateRangeForm.invalid) {
            this.screenFilterForm.setErrors({ dateRangeInvalid: true });
          } else {
            this.screenFilterForm.setErrors(null);
          }

          if (startDate) this.chosenDates.emit();
        },
        (error: Error) => {
          console.error(error);
        }
      );
  }

  public resetDateRange(): void {
    this.dateRangeForm.reset(
      {
        startDate: null,
        endDate: null,
      },
      { emitEvent: false }
    );
  }

  get dateRangeError(): boolean {
    const start = this.dateRangeForm.get('startDate');
    const end = this.dateRangeForm.get('endDate');

    const hasRequiredError = start?.hasError('required') || end?.hasError('required');

    const isTouched = start?.touched || end?.touched;

    return !!(hasRequiredError && isTouched);
  }
}
